<template>
  <iColumn>
    <iColumn gap="none">
      <iText variant="subtle" font-size="small">
        Customize when and how you receive notifications
      </iText>
      <iButton
        v-if="!hideResetBtn"
        variant="tertiary"
        size="extraSmall"
        :is-loading="loadingReset"
        @click="$emit('reset-to-default')"
      >
        (Reset to defaults)
      </iButton>
    </iColumn>

    <iColumn>
      <NotificationToggle
        :model-value="modelValue.notify_on_conversation_message"
        title="Conversation messages"
        description="Get notified when someone sends a message in a conversation"
        @update:model-value="(value) => updateModelValue({notify_on_conversation_message: value})"
      />
      <NotificationToggle
        :model-value="modelValue.notify_on_conversation_mention"
        title="Mentions"
        description="Get notified when someone mentions you in a conversation"
        @update:model-value="(value) => updateModelValue({notify_on_conversation_mention: value})"
      />
      <NotificationToggle
        :model-value="modelValue.notify_on_channel_call"
        title="Channel calls"
        description="Get notified when someone starts a call in a channel"
        @update:model-value="(value) => updateModelValue({notify_on_channel_call: value})"
      />
      <NotificationToggle
        :model-value="modelValue.notify_on_thread_reply"
        title="Thread replies"
        description="Get notified when someone replies to a thread you're following"
        @update:model-value="(value) => updateModelValue({notify_on_thread_reply: value})"
      />
    </iColumn>

    <iColumn v-if="!hideNotificationSchedule">
      <iDivider />

      <iColumn gap="none">
        <iText font-weight="dark" font-size="large">
          Notification Schedule
        </iText>
        <iText variant="subtle" font-size="small">
          Only receive notifications on these days
        </iText>
      </iColumn>
      <iColumn>
        <iRow wrap="nowrap" vertical-align="middle">
          <iButton
            :variant="scheduleType === 'allDay' ? 'primary' : 'secondary'"
            size="small"
            @click="setScheduleType('allDay')"
          >
            All Day
          </iButton>
          <iButton
            :variant="scheduleType === 'weekdays' ? 'primary' : 'secondary'"
            size="small"
            @click="setScheduleType('weekdays')"
          >
            Weekdays
          </iButton>
          <iButton
            :variant="scheduleType === 'custom' ? 'primary' : 'secondary'"
            size="small"
            @click="setScheduleType('custom')"
          >
            Custom
          </iButton>
        </iRow>

        <iColumn v-if="scheduleType === 'custom'">
          <iText>
            Which days would you like to be notified on
          </iText>
          <iRow vertical-align="middle">
            <iButton
              v-for="day in days"
              :key="day.value"
              size="small"
              :variant="activeSchedule.includes(day.value) ? 'primary' : 'secondary'"
              @click="toggleDay(day.value)"
            >
              {{ day.name }}
            </iButton>
          </iRow>
        </iColumn>
      </iColumn>
    </iColumn>

    <iColumn v-if="!hideNotificationHours">
      <iDivider />

      <iColumn gap="none">
        <iText font-weight="dark" font-size="large">
          Notification Hours
        </iText>
        <iText variant="subtle" font-size="small">
          Only receive notifications during these hours
        </iText>
      </iColumn>

      <iRow vertical-align="middle">
        <iRow wrap="nowrap" vertical-align="middle" width="hug">
          <iText>From</iText>
          <iTextInput
            :model-value="modelValue.notification_start_time"
            size="small"
            type="time"
            :width="200"
            @update:model-value="updateStartTime"
          />
        </iRow>
        <iRow wrap="nowrap" vertical-align="middle" width="hug">
          <iText>To</iText>
          <iTextInput
            :model-value="modelValue.notification_end_time"
            size="small"
            type="time"
            :width="200"
            @update:model-value="updateEndTime"
          />
        </iRow>
      </iRow>
    </iColumn>
  </iColumn>
</template>

<script>
import NotificationToggle from "~/components/notifications/NotificationToggle.vue";

export default {
  name: "NotificationSettings",
  components: { NotificationToggle },
  props: {
    modelValue: {
      type: Object,
      default: () => ({}),
    },
    hideNotificationSchedule: {
      type: Boolean,
      default: false,
    },
    hideNotificationHours: {
      type: Boolean,
      default: false,
    },
    hideResetBtn: {
      type: Boolean,
      default: false,
    },
    loadingReset: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:model-value", "reset-to-default"],
  data() {
    return {
      settings: {},
      scheduleType: "weekdays",
      days: [
        { value: 1, name: "Monday", short: "M" },
        { value: 2, name: "Tuesday", short: "T" },
        { value: 3, name: "Wednesday", short: "W" },
        { value: 4, name: "Thursday", short: "T" },
        { value: 5, name: "Friday", short: "F" },
        { value: 6, name: "Saturday", short: "S" },
        { value: 7, name: "Sunday", short: "S" },
      ],
    };
  },
  computed: {
    activeSchedule() {
      return this.modelValue.active_schedule ?? [];
    },
  },
  watch: {
    modelValue: {
      deep: true,
      handler() {
        this.init();
      },
      immediate: true,
    },
  },
  methods: {
    init() {
      const schedule = this.modelValue.active_schedule ?? [];

      if (schedule.length === 7) {
        this.scheduleType = "allDay";
      } else if (schedule.length === 5 &&
          schedule.includes(1) && schedule.includes(2) &&
          schedule.includes(3) && schedule.includes(4) &&
          schedule.includes(5)) {
        this.scheduleType = "weekdays";
      } else {
        this.scheduleType = "custom";
      }
    },
    updateModelValue(updates) {
      this.$emit("update:model-value", {
        ...this.modelValue,
        ...updates,
      });
    },
    setScheduleType(type) {
      let activeSchedule;

      switch (type) {
        case "allDay":
          this.scheduleType = "allDay";
          activeSchedule = [1, 2, 3, 4, 5, 6, 7];
          break;
        case "weekdays":
          this.scheduleType = "weekdays";
          activeSchedule = [1, 2, 3, 4, 5];
          break;
        case "custom":
          this.scheduleType = "custom";
          activeSchedule = this.modelValue.active_schedule ?? [];
          break;
      }

      if (activeSchedule) {
        this.updateModelValue({ active_schedule: activeSchedule });
      }
    },
    toggleDay(dayValue) {
      const schedule = [...this.modelValue.active_schedule ?? []];
      const index = schedule.indexOf(dayValue);

      if (index > -1) {
        schedule.splice(index, 1);
      } else {
        schedule.push(dayValue);
        schedule.sort((a, b) => a - b);
      }

      this.updateModelValue({ active_schedule: schedule });
    },
    updateStartTime(value) {
      this.updateModelValue({ notification_start_time: value });
    },
    updateEndTime(value) {
      this.updateModelValue({ notification_end_time: value });
    },
  },
};
</script>

<style scoped>
</style>
